import React, { useRef } from 'react'
import { IDropdownPosition } from './TranslationLanguages'
import GuidelinesDropdown from './GuidelinesDropdown'
import AddScriptButton from './AddScriptButton'
import Arrow from '@Images/languages_dropdown_arrow.svg'
import Remove from '@Images/project_setup_form_remove_language.svg'
import ScriptFile from '@Images/project_setup_form_script_file.svg'

import {
  ITranslationSettings,
} from '@SiteModules/Project/types'

import {
  IGuideline,
} from '@SiteModules/Guideline/types'

interface TranslationLanguageProps {
  readonly index: number
  readonly currentNewTranslationSettings: ITranslationSettings
  readonly newTranslationSettings: ITranslationSettings[]
  readonly indexForReplacingTranslationLanguages: number
  readonly dropdownOffsetY: number
  readonly translationGuidelines: IGuideline[]
  readonly cannotImportScript: boolean
  setIndexForReplacingTranslationLanguages(newIndexForReplacingTranslationLanguages: number): void
  setNewTranslationSettings(newTranslationSettings: ITranslationSettings[]): void
  renderSearchInput(currentTranslationLanguage?: string): React.JSX.Element
  renderTranslationLanguages(style: IDropdownPosition, languageIndexToReplace?: number): React.JSX.Element
}

const TranslationLanguage: React.FC<TranslationLanguageProps> = props => {
  const {
    index,
    newTranslationSettings,
    currentNewTranslationSettings,
    dropdownOffsetY,
    indexForReplacingTranslationLanguages,
    translationGuidelines,
    cannotImportScript,
  } = props

  const {
    language,
    translationGuidelineOption,
    srtTranslationText,
    scriptFileName,
  } = currentNewTranslationSettings

  const fileRef = useRef<HTMLInputElement>(null)
  const shouldShowDropdown = indexForReplacingTranslationLanguages === index
  const willUseScript = !!srtTranslationText && srtTranslationText.length > 0

  const openTranslationLanguages = () => {
    props.setIndexForReplacingTranslationLanguages(index)
  }

  const handleRemoveTranslationLanguage = () => {
    props.setNewTranslationSettings([...newTranslationSettings.slice(0, index), ...newTranslationSettings.slice(index + 1)])
    if (fileRef.current) {
      fileRef.current.value = ''
    }
  }

  const setTranslationGuidelineOption = (newTranslationGuidelineOption: string | null) => {
    props.setNewTranslationSettings([...newTranslationSettings.slice(0, index), { ...currentNewTranslationSettings, translationGuidelineOption: newTranslationGuidelineOption }, ...newTranslationSettings.slice(index + 1)])
  }

  const setScriptTextAndFileName = (newScriptText: string, newScriptFileName: string | null) => {
    props.setNewTranslationSettings([...newTranslationSettings.slice(0, index), { ...currentNewTranslationSettings, scriptFileName: newScriptFileName, srtTranslationText: newScriptText }, ...newTranslationSettings.slice(index + 1)])
  }

  const openFileManager = () => {
    if (fileRef.current) {
      fileRef.current.value = ''
    }

    fileRef.current?.click()
  }

  const resetScriptUpload = () => {
    setScriptTextAndFileName('', null)
    if (fileRef.current) {
      fileRef.current.value = ''
    }
  }

  const renderCurrentItem = () => {
    return (
      <div
        className="SearchDropdown__current-item"
        onClick={openTranslationLanguages}
      >
        <div className="SearchDropdown__current-item-name">
          {language}
        </div>
        <Arrow />
      </div>
    )
  }

  const renderButtons = () => {
    return (
      <div className="SetupProjectPopup__buttons">
        {renderAddScriptButton()}
        {renderGuidelineButton()}
        {renderRemoveButton()}
      </div>
    )
  }

  const renderAddScriptButton = () => {
    return (
      <AddScriptButton
        cannotImportScript={cannotImportScript}
        fileRef={fileRef}
        setScriptTextAndFileName={setScriptTextAndFileName}
        resetScriptUpload={resetScriptUpload}
        openFileManager={openFileManager}
        isForTranslation
      />
    )
  }

  const renderGuidelineButton = () => {
    return (
      <GuidelinesDropdown
        guidelines={translationGuidelines}
        guidelineOption={translationGuidelineOption || null}
        setGuidelineOption={setTranslationGuidelineOption}
      />
    )
  }

  const renderRemoveButton = () => {
    return (
      <div
        className="SetupProjectPopup__button"
        onClick={handleRemoveTranslationLanguage}
      >
        <Remove />
      </div>
    )
  }

  const renderScript = () => {
    return (
      <div className="SetupProjectPopup__script-wrapper">
        <div
          className="SetupProjectPopup__script"
          onClick={openFileManager}
        >
          <ScriptFile />
          <div className="SetupProjectPopup__script-name">
            {scriptFileName}
          </div>
          <div className="SetupProjectPopup__script-srt">
            SRT File
          </div>
        </div>
        <div
          className="SetupProjectPopup__button"
          onClick={resetScriptUpload}
        >
          <Remove />
        </div>
      </div>
    )
  }

  const newTranslationLanguageDropdownParentElement = document.getElementById(`translation_languages_new_translations-item-${index}`)
  let newTranslationLanguageDropdownX = 0
  let newTranslationLanguageDropdownY = 0

  if (newTranslationLanguageDropdownParentElement) {
    const newTranslationLanguageDropdownParentElementBounds = newTranslationLanguageDropdownParentElement.getBoundingClientRect()

    newTranslationLanguageDropdownX = newTranslationLanguageDropdownParentElementBounds.left
    newTranslationLanguageDropdownY = newTranslationLanguageDropdownParentElementBounds.top + dropdownOffsetY
  }

  const newTranslationLanguageDropdownPositioningStyle = {
    top: `${newTranslationLanguageDropdownY}px`,
    left: `${newTranslationLanguageDropdownX}px`,
  }

  return (
    <li
      className="TranslationLanguages__new-translations-item"
      id={`translation_languages_new_translations-item-${index}`}
    >
      <div className="TranslationLanguages__new-translations-item-main TranslationLanguages__new-translations-item-element">
        {shouldShowDropdown ? (
          <React.Fragment>
            {props.renderSearchInput(currentNewTranslationSettings.language)}
            {props.renderTranslationLanguages(newTranslationLanguageDropdownPositioningStyle, index)}
          </React.Fragment>
        ) : (
          <React.Fragment>
            {renderCurrentItem()}
            {renderButtons()}
          </React.Fragment>
        )}
      </div>
      {willUseScript && renderScript()}
    </li>
  )
}

export default TranslationLanguage
